/**
 * PublicProfileSection - Gestione profilo pubblico aziendale
 * Permette ai seller di abilitare una pagina pubblica con URL personalizzata
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import {
  Globe,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Loader2,
  Copy,
  Eye,
  Info,
} from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

interface CompanyData {
  id: string;
  name: string;
  public_slug?: string;
  public_profile_enabled?: boolean;
}

const slugify = (text: string) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 50);

export default function PublicProfileSection() {
  const [company, setCompany] = useState<CompanyData | null>(null);
  const [slug, setSlug] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await axios.get(`${API}/companies/my`);
        setCompany(res.data);
        setSlug(res.data.public_slug || '');
        setEnabled(res.data.public_profile_enabled || false);
      } catch {
        toast.error('Errore nel caricamento dei dati aziendali');
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, []);

  const checkSlug = useCallback(async (value: string) => {
    if (!value || value === company?.public_slug) {
      setSlugStatus('idle');
      return;
    }
    setSlugStatus('checking');
    try {
      const res = await axios.get(`${API}/companies/check-slug/${value}`);
      setSlugStatus(res.data.available ? 'available' : 'taken');
    } catch (err: any) {
      setSlugStatus('invalid');
    }
  }, [company?.public_slug]);

  useEffect(() => {
    if (!slug) { setSlugStatus('idle'); return; }
    const timer = setTimeout(() => checkSlug(slug), 600);
    return () => clearTimeout(timer);
  }, [slug, checkSlug]);

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = slugify(e.target.value);
    setSlug(val);
  };

  const handleSuggestSlug = () => {
    if (company?.name) setSlug(slugify(company.name));
  };

  const handleSave = async () => {
    if (slug && slugStatus === 'taken') {
      toast.error('Questo slug è già in uso da un\'altra azienda');
      return;
    }
    if (slug && slugStatus === 'invalid') {
      toast.error('Slug non valido');
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = { public_profile_enabled: enabled };
      if (slug) payload.public_slug = slug;
      await axios.patch(`${API}/companies/my`, payload);
      setCompany(prev => prev ? { ...prev, public_slug: slug, public_profile_enabled: enabled } : null);
      toast.success('Profilo pubblico aggiornato');
    } catch {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = slug ? `${window.location.origin}/p/${slug}` : null;

  const handleCopyUrl = () => {
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      toast.success('URL copiata negli appunti');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Profilo Pubblico</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Crea una pagina pubblica della tua azienda accessibile senza login.
        </p>
      </div>

      {/* Attiva/Disattiva */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary" />
            Visibilità Pubblica
          </CardTitle>
          <CardDescription>
            Quando attivato, la tua pagina sarà visibile a chiunque abbia il link.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="public-profile-toggle" className="font-medium">
                Abilita profilo pubblico
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Mostra nome, descrizione, contatti e categorie
              </p>
            </div>
            <Switch
              id="public-profile-toggle"
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="public-profile-toggle"
            />
          </div>

          <Separator />

          {/* Slug */}
          <div className="space-y-2">
            <Label htmlFor="public-slug">
              URL personalizzata
            </Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm select-none pointer-events-none">
                  /p/
                </span>
                <Input
                  id="public-slug"
                  value={slug}
                  onChange={handleSlugChange}
                  placeholder="nome-azienda"
                  className="pl-8 font-mono text-sm"
                  data-testid="public-slug-input"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {slugStatus === 'checking' && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  {slugStatus === 'available' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                  {(slugStatus === 'taken' || slugStatus === 'invalid') && <XCircle className="w-4 h-4 text-destructive" />}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSuggestSlug}
                data-testid="suggest-slug-btn"
              >
                Suggerisci
              </Button>
            </div>

            {/* Stato slug */}
            {slugStatus === 'available' && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Slug disponibile
              </p>
            )}
            {slugStatus === 'taken' && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <XCircle className="w-3.5 h-3.5" /> Slug già in uso, scegli un altro
              </p>
            )}
            {slugStatus === 'invalid' && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <XCircle className="w-3.5 h-3.5" /> Formato non valido (solo lettere minuscole, numeri, trattini, min 3 caratteri)
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Solo lettere minuscole, numeri e trattini (es. <code className="bg-muted px-1 rounded">caseificio-il-buono</code>)
            </p>
          </div>

          {/* Preview URL */}
          {publicUrl && company?.public_slug === slug && (
            <div className="bg-muted/50 rounded-lg p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Eye className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm font-mono truncate text-muted-foreground">{publicUrl}</span>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleCopyUrl}
                  data-testid="copy-url-btn"
                >
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <a
                  href={`/p/${slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-profile-link"
                >
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </a>
              </div>
            </div>
          )}

          {/* Info box */}
          {enabled && !slug && (
            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription className="text-xs">
                Imposta uno slug per attivare la pagina pubblica.
              </AlertDescription>
            </Alert>
          )}

          {/* Save */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || (!!slug && (slugStatus === 'taken' || slugStatus === 'invalid' || slugStatus === 'checking'))}
              data-testid="save-public-profile-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Salva modifiche
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info su cosa viene mostrato */}
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-muted-foreground font-normal flex items-center gap-2">
            <Info className="w-4 h-4" />
            Cosa viene mostrato nella pagina pubblica
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-xs text-muted-foreground space-y-1.5 list-none">
            {[
              'Logo aziendale',
              'Nome e tipo di azienda',
              'Descrizione',
              'Città e provincia',
              'Telefono, email e sito web',
              'Categorie/settori',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground mt-3">
            I dati vengono prelevati dalla sezione <strong>Azienda</strong> delle impostazioni.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
